import { useState } from "react";
import {
  Alert,
  Badge,
  Card,
  DataTable,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
} from "@empite/ui-components";
import type {
  Operator,
  PriceProposal,
  PriceProposalStatus,
  RateDelta,
  Reason,
} from "../admin-client";
import {
  usePriceProposals,
  usePublishPriceProposal,
  useRejectPriceProposal,
} from "../admin-hooks";
import { ErrorAlert, RequireOperator, can } from "./guards";
import { ReasonButton } from "./reason-prompt";
import { StatusBadge, when } from "./common";

/** The permission the Admin API guards publish and reject with. Repeated here
 *  only to decide what to draw — the server is the control, and a read-only
 *  admin who forged the request would still be refused and audited. */
const WRITE = "pricing:write";

/** The flag vocabulary internal/billing/pricemap/source.go can raise, and what
 *  each one is asking the operator to decide.
 *
 *  The column is open text server-side so a new check needs no migration,
 *  which means an unknown value has to render as ITSELF rather than be
 *  dropped: a flag this build has never heard of is exactly the one worth
 *  showing. `flagText` falls through to the raw string for that reason. */
const FLAG_TEXT: Record<string, string> = {
  zero_rate: "A priced dimension came through as free — either the vendor does not bill it, or the field is missing upstream.",
  large_increase: "A dimension rose by 50% or more against the rate in force.",
  large_decrease: "A dimension fell by 50% or more against the rate in force.",
  implausible_rate: "A rate is beyond the sanity bound. Read the source document before publishing this.",
  unmatched_upstream: "The source named a model key the catalogue does not carry, so the match was made on the alias.",
};

function flagText(flag: string): string {
  return FLAG_TEXT[flag] ?? flag;
}

/** The six dimensions, in the order the rate card is read. */
const DIMENSION_LABELS: Record<string, string> = {
  input_per_million: "Input / M",
  cached_input_per_million: "Cached input / M",
  cache_write_per_million: "Cache write / M",
  output_per_million: "Output / M",
  reasoning_per_million: "Reasoning / M",
  request_fee: "Request fee",
};

const STATUS_OPTIONS: Array<{ value: PriceProposalStatus | "any"; label: string }> = [
  { value: "proposed", label: "Awaiting review" },
  { value: "published", label: "Published" },
  { value: "rejected", label: "Rejected" },
  { value: "superseded", label: "Superseded by a later fetch" },
  { value: "any", label: "Everything" },
];

/**
 * Basis points as a signed percentage — for a PER-DIMENSION delta, where the
 * direction is the point: a rate that halved and a rate that doubled are not
 * the same news.
 *
 * The DIVISION is the only arithmetic this screen does on a number the server
 * sent, and it is safe because basis points are already a small integer — the
 * rates themselves stay decimal strings all the way to the screen, because
 * parsing "0.000015" into a float loses the digits that make a per-million
 * rate a rate at all.
 */
function percent(bps: number): string {
  const sign = bps > 0 ? "+" : "";
  return `${sign}${(bps / 100).toFixed(2)}%`;
}

/**
 * The same division WITHOUT a sign, for `largest_move_bps`.
 *
 * PriceProposal.LargestMoveBps returns a MAGNITUDE — the biggest absolute
 * per-dimension move, so an operator approving sixteen rates reads one number
 * and it is the worst one. Running that through `percent` printed a 66% price
 * CUT as "+66.66%", which is the one direction an operator must not read
 * backwards on a confirmation dialog. The sign belongs to the per-dimension
 * table, where it is not synthesised.
 */
function magnitude(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}

function moveTone(bps: number): "neutral" | "warning" | "danger" {
  const magnitude = Math.abs(bps);
  if (magnitude >= 5_000) {
    return "danger";
  }
  return magnitude >= 1_000 ? "warning" : "neutral";
}

/** The worst move in a set of proposals, which is what a publish confirmation
 *  has to state: an operator approving sixteen rates reads one number, and it
 *  should be the worst one. */
function worstMove(proposals: PriceProposal[]): number | undefined {
  let worst: number | undefined;
  for (const p of proposals) {
    if (p.largest_move_bps === undefined) {
      continue;
    }
    // A plain max, not a max-by-absolute: every value here is already a
    // magnitude. See magnitude().
    if (worst === undefined || p.largest_move_bps > worst) {
      worst = p.largest_move_bps;
    }
  }
  return worst;
}

function describeMove(bps: number | undefined): string {
  return bps === undefined
    ? "no rate in force to move from"
    : `the largest move is ${magnitude(bps)}`;
}

/**
 * The price proposal queue.
 *
 * This screen is the seam between a vendor's published list price and the
 * price list this platform charges from. Everything on it is a CLAIM until an
 * operator publishes it: the resolver reads billing.provider_price_versions
 * and never provider_price_proposals, so no amount of clicking here moves a
 * quote except through the two audited writes.
 *
 * Grouped by vendor because that is the unit an operator reviews in — a price
 * map refresh moves one vendor's whole rate card at once, and sixteen rows
 * from four vendors interleaved is a list nobody can sign off on.
 */
export function PriceProposals() {
  return (
    <RequireOperator>{(session) => <ProposalQueue operator={session.operator} />}</RequireOperator>
  );
}

function ProposalQueue({ operator }: { operator: Operator }) {
  const [status, setStatus] = useState<PriceProposalStatus | "any">("proposed");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  // Empty means now, and it stays empty by default: publishing immediately is
  // the ordinary case, and a console that synthesised a timestamp from the
  // browser clock would be a console whose clock decides when a price changes.
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [notice, setNotice] = useState<string | null>(null);

  const writable = can(operator, WRITE);
  const proposals = usePriceProposals({ status, flagged: flaggedOnly || undefined });

  const rows = proposals.data?.price_proposals ?? [];
  const vendors = groupByVendor(rows);

  return (
    <>
      <PageHeader
        title="Price review"
        description="Vendor list prices as the fetcher read them. Nothing here prices a request until it is published."
      />

      {notice ? <Alert tone="success" title={notice} /> : null}

      {writable ? null : (
        <Alert tone="info" title="You can read this queue but not decide it">
          Publishing a rate and rejecting one both need <code>{WRITE}</code>. Ask an operator who
          holds it to make the call.
        </Alert>
      )}

      <Card title="Queue">
        <div className="esm-filters">
          <Field label="Status" htmlFor="proposal-status">
            <Select
              id="proposal-status"
              value={status}
              onChange={(event) => setStatus(event.target.value as PriceProposalStatus | "any")}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Flagged only"
            htmlFor="proposal-flagged"
            hint="The rates a check asked a person to look at."
          >
            <Input
              id="proposal-flagged"
              type="checkbox"
              checked={flaggedOnly}
              onChange={(event) => setFlaggedOnly(event.target.checked)}
            />
          </Field>
          <Field
            label="Publish from"
            htmlFor="proposal-effective-from"
            hint="Leave blank to start the new rate now. A past instant is refused: requests already settled were quoted under the rate in force at the time."
          >
            <Input
              id="proposal-effective-from"
              type="datetime-local"
              value={effectiveFrom}
              onChange={(event) => setEffectiveFrom(event.target.value)}
            />
          </Field>
        </div>
      </Card>

      {proposals.isPending ? (
        <Spinner label="Loading the price proposals" />
      ) : proposals.error ? (
        <ErrorAlert error={proposals.error} title="Could not load the price proposals" />
      ) : vendors.length === 0 ? (
        <Card title="Nothing to review">
          <p className="esm-muted-text">
            No proposal matches this filter. A <code>pricing.fetch</code> run that read an unchanged
            price map writes nothing, so an empty queue is the ordinary state.
          </p>
        </Card>
      ) : (
        vendors.map((vendor) => (
          <VendorGroup
            key={vendor.provider}
            provider={vendor.provider}
            proposals={vendor.proposals}
            writable={writable}
            effectiveFrom={effectiveFrom}
            onDone={setNotice}
          />
        ))
      )}
    </>
  );
}

interface VendorRows {
  provider: string;
  proposals: PriceProposal[];
}

/** Vendors alphabetically, models alphabetically inside each. A stable order
 *  is what lets an operator compare this refresh against the last one. */
function groupByVendor(rows: PriceProposal[]): VendorRows[] {
  const byVendor = new Map<string, PriceProposal[]>();
  for (const row of rows) {
    const bucket = byVendor.get(row.provider);
    if (bucket) {
      bucket.push(row);
    } else {
      byVendor.set(row.provider, [row]);
    }
  }
  return [...byVendor.entries()]
    .map(([provider, proposals]) => ({
      provider,
      proposals: [...proposals].sort((a, b) => a.model.localeCompare(b.model)),
    }))
    .sort((a, b) => a.provider.localeCompare(b.provider));
}

/**
 * The datetime-local value as RFC3339, or undefined for "now".
 *
 * `new Date(value)` reads a datetime-local string in the BROWSER's zone, which
 * is the zone the operator typed it in, and toISOString converts it to UTC.
 * Getting that backwards would schedule an Australian operator's 9am price
 * change ten hours early.
 */
function rfc3339(local: string): string | undefined {
  const trimmed = local.trim();
  if (trimmed === "") {
    return undefined;
  }
  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

function VendorGroup({
  provider,
  proposals,
  writable,
  effectiveFrom,
  onDone,
}: {
  provider: string;
  proposals: PriceProposal[];
  writable: boolean;
  effectiveFrom: string;
  onDone: (message: string) => void;
}) {
  const publish = usePublishPriceProposal();
  const reject = useRejectPriceProposal();

  // Only a `proposed` row can be decided. A queue filtered to `published`
  // still renders — an operator checking what went out last night needs it —
  // but it offers no buttons, because the server refuses a second review with
  // a 409 and meeting that as a dialog is worse than not offering it.
  const pending = proposals.filter((p) => p.status === "proposed");
  const flagged = proposals.filter((p) => p.flagged).length;
  const worst = worstMove(pending);

  async function publishAll(reason: Reason) {
    const at = rfc3339(effectiveFrom);
    let done = 0;
    // Sequential, not Promise.all: each publish closes the previous version
    // for its pair and re-checks that no routable model is left unpriced, and
    // sixteen of those racing would have sixteen different answers to that
    // question. A failure stops the run and says how far it got, because
    // "seven of sixteen published" is the thing the operator has to know.
    for (const proposal of pending) {
      await publish.mutateAsync({ proposalID: proposal.id, body: { effective_from: at }, reason });
      done += 1;
    }
    onDone(
      `Published ${done} ${done === 1 ? "rate" : "rates"} for ${provider}${
        at ? `, effective ${new Date(at).toLocaleString()}` : ", effective now"
      }.`,
    );
  }

  return (
    <Card
      title={provider}
      actions={
        writable && pending.length > 0 ? (
          <ReasonButton
            variant="primary"
            busy={publish.isPending}
            mutation={{
              title: `Publish ${pending.length} ${provider} ${pending.length === 1 ? "rate" : "rates"}`,
              confirmLabel: "Publish",
              tone: "primary",
              description: (
                <>
                  <p>
                    This prices <strong>{pending.length}</strong>{" "}
                    {pending.length === 1 ? "model" : "models"} from <strong>{provider}</strong>, and{" "}
                    {describeMove(worst)}.
                  </p>
                  <p>
                    Each new rate closes the one it replaces at exactly the instant it begins.
                    Nothing already settled is repriced — a settled charge stays pinned to the
                    version it was quoted under.
                  </p>
                </>
              ),
              run: async (reason) => {
                await publishAll(reason);
              },
            }}
          >
            Publish all ({pending.length})
          </ReasonButton>
        ) : null
      }
    >
      <p className="esm-muted-text">
        {proposals.length} {proposals.length === 1 ? "proposal" : "proposals"}
        {pending.length === proposals.length ? "" : `, ${pending.length} awaiting review`}
        {flagged > 0 ? `, ${flagged} flagged` : ""}.
      </p>

      <ErrorAlert error={publish.error} title="That rate was not published" />
      <ErrorAlert error={reject.error} title="That proposal was not rejected" />

      {proposals.map((proposal) => (
        <ProposalBlock
          key={proposal.id}
          proposal={proposal}
          writable={writable}
          effectiveFrom={effectiveFrom}
          publishBusy={publish.isPending}
          rejectBusy={reject.isPending}
          onPublish={async (reason) => {
            const at = rfc3339(effectiveFrom);
            await publish.mutateAsync({
              proposalID: proposal.id,
              body: { effective_from: at },
              reason,
            });
            onDone(
              `Published ${proposal.provider} ${proposal.model}${
                at ? `, effective ${new Date(at).toLocaleString()}` : ", effective now"
              }.`,
            );
          }}
          onReject={async (reason) => {
            await reject.mutateAsync({ proposalID: proposal.id, reason });
            onDone(`Rejected ${proposal.provider} ${proposal.model}. Nothing was repriced.`);
          }}
        />
      ))}
    </Card>
  );
}

function ProposalBlock({
  proposal,
  writable,
  effectiveFrom,
  publishBusy,
  rejectBusy,
  onPublish,
  onReject,
}: {
  proposal: PriceProposal;
  writable: boolean;
  effectiveFrom: string;
  publishBusy: boolean;
  rejectBusy: boolean;
  onPublish: (reason: Reason) => Promise<void>;
  onReject: (reason: Reason) => Promise<void>;
}) {
  const decidable = writable && proposal.status === "proposed";
  const at = rfc3339(effectiveFrom);

  return (
    <section className="esm-subform">
      <h3>
        <code>{proposal.model}</code> <span className="esm-muted-text">{proposal.currency}</span>
      </h3>
      <div className="esm-badge-row">
        <StatusBadge status={proposal.status} />
        {proposal.flagged ? <Badge tone="warning">flagged</Badge> : null}
        {proposal.largest_move_bps === undefined ? null : (
          <Badge tone={moveTone(proposal.largest_move_bps)}>
            {/* "moves 66.66%", never "+66.66%" — see magnitude(). Which way
                each dimension moved is in the table below. */}
            moves {magnitude(proposal.largest_move_bps)}
          </Badge>
        )}
      </div>
      <div>
        {proposal.flags.length > 0 ? (
          <Alert tone="warning" title="A check asked for a person here">
            <ul>
              {proposal.flags.map((flag) => (
                <li key={flag}>
                  <code>{flag}</code> — {flagText(flag)}
                </li>
              ))}
            </ul>
          </Alert>
        ) : null}

        {proposal.in_force ? (
          <DeltaTable deltas={proposal.deltas} />
        ) : (
          <>
            <Alert tone="info" title="This pair has no price in force">
              Nothing is being replaced, so there is no difference to read. Publishing this is what
              makes the model quotable at all.
            </Alert>
            <DataTable<{ dimension: string; amount: string }>
              rows={Object.keys(DIMENSION_LABELS).map((dimension) => ({
                dimension,
                amount: rateOf(proposal, dimension),
              }))}
              rowKey={(row) => row.dimension}
              columns={[
                {
                  key: "dimension",
                  header: "Dimension",
                  render: (row) => DIMENSION_LABELS[row.dimension] ?? row.dimension,
                },
                {
                  key: "amount",
                  header: "Proposed",
                  align: "end",
                  render: (row) => <code>{row.amount}</code>,
                },
              ]}
            />
          </>
        )}

        <dl className="esm-detail-list">
          <dt>Source</dt>
          <dd>
            {proposal.source_name} <span className="esm-muted-text">({proposal.source_slug})</span>
          </dd>
          <dt>Fingerprint</dt>
          <dd>
            {/* The whole digest, not a prefix: this is what ties a published
                rate back to the exact document it was read out of, and a
                truncated one cannot be compared against the file. */}
            <code>{proposal.source_fingerprint}</code>
          </dd>
          <dt>Fetched</dt>
          <dd>{when(proposal.fetched_at)}</dd>
          {proposal.in_force_effective_from ? (
            <>
              <dt>In force since</dt>
              <dd>{when(proposal.in_force_effective_from)}</dd>
            </>
          ) : null}
          {proposal.upstream_key ? (
            <>
              <dt>Source model key</dt>
              <dd>
                <code>{proposal.upstream_key}</code>
              </dd>
            </>
          ) : null}
          {proposal.reviewed_at ? (
            <>
              <dt>Reviewed</dt>
              <dd>
                {when(proposal.reviewed_at)}
                {proposal.review_reason_code ? (
                  <span className="esm-muted-text"> — {proposal.review_reason_code}</span>
                ) : null}
              </dd>
            </>
          ) : null}
        </dl>

        {decidable ? (
          <div className="esm-actions">
            <ReasonButton
              variant="primary"
              busy={publishBusy}
              mutation={{
                title: `Publish ${proposal.provider} ${proposal.model}`,
                confirmLabel: "Publish",
                tone: "primary",
                description: (
                  <>
                    <p>
                      This prices <strong>1</strong> model, and{" "}
                      {describeMove(proposal.largest_move_bps)}. It takes effect{" "}
                      {at ? new Date(at).toLocaleString() : "now"}.
                    </p>
                    <p>
                      The rate it replaces closes at exactly that instant, so there is no moment
                      with two rates and no moment with none. Nothing already settled is repriced.
                    </p>
                  </>
                ),
                run: onPublish,
              }}
            >
              Publish
            </ReasonButton>
            <ReasonButton
              busy={rejectBusy}
              mutation={{
                title: `Reject ${proposal.provider} ${proposal.model}`,
                confirmLabel: "Reject",
                description: (
                  <p>
                    The rate in force does not change and nothing is repriced. The reason code
                    below is the record of why this vendor rate was not taken.
                  </p>
                ),
                run: onReject,
              }}
            >
              Reject
            </ReasonButton>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function rateOf(proposal: PriceProposal, dimension: string): string {
  const rates = proposal.proposed as unknown as Record<string, string>;
  return rates[dimension] ?? "0";
}

/**
 * The per-dimension diff, printed exactly as the wire sent it.
 *
 * Every amount here is a decimal string the server computed on integer micros
 * — the portal never subtracts two rates, because money arithmetic in
 * TypeScript is float arithmetic and a rate card is the last place to do it.
 */
function DeltaTable({ deltas }: { deltas: RateDelta[] }) {
  return (
    <DataTable<RateDelta>
      rows={deltas}
      rowKey={(row) => row.dimension}
      empty="The proposed rates are identical to the ones in force."
      columns={[
        {
          key: "dimension",
          header: "Dimension",
          render: (row) => DIMENSION_LABELS[row.dimension] ?? row.dimension,
        },
        {
          key: "from",
          header: "In force",
          align: "end",
          render: (row) => <code>{row.from}</code>,
        },
        {
          key: "to",
          header: "Proposed",
          align: "end",
          render: (row) => <code>{row.to}</code>,
        },
        {
          key: "delta",
          header: "Change",
          align: "end",
          render: (row) =>
            row.delta === "0" || Number(row.delta) === 0 ? (
              <span className="esm-muted-text">unchanged</span>
            ) : (
              <code>{row.delta}</code>
            ),
        },
        {
          key: "percent",
          header: "%",
          align: "end",
          render: (row) =>
            row.percent_bps === undefined ? (
              // No proportion of a zero rate. Printing "∞%" would be worse
              // than printing the two rates and letting the operator read them.
              <span className="esm-muted-text">—</span>
            ) : (
              <Badge tone={moveTone(row.percent_bps)}>{percent(row.percent_bps)}</Badge>
            ),
        },
      ]}
    />
  );
}
